import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { countryConfig } from '../country';
import { LocaleLink as Link, LocaleNavLink as NavLink } from '../i18n/locale-link';
import { useAuth } from '../lib/auth-context';
import { useAuthOrigin } from '../lib/auth-origin';
import { useT, useLocale } from '../i18n/locale-context';
import { userDisplayName, userInitials, isPaidUser, planLabel } from '../lib/user-display';
import { ProBadge } from './pro-badge';
import { fetchCatalog } from '../lib/catalog-client';
import { listSavedTenders } from '../lib/saved-tenders-client';
import { fetchNewMatchCount, readLastVisit } from '../lib/my-tenders-client';
import {
  listSavedSearches,
  savedSearchToCatalogPath,
  type SavedSearch,
} from '../lib/saved-search-client';
import { SavedSearchEditSheet } from './saved-search-edit-sheet';
import { useDeleteSavedSearch, useSavedSearchDeletePrompt } from './saved-search-delete';
import {
  ArrowRight,
  BankIcon,
  BuildingIcon,
  BookIcon,
  BellIcon,
  BrandTile,
  ChartIcon,
  ChatIcon,
  InfoIcon,
  ListIcon,
  PackageIcon,
  StarIcon,
  TagIcon,
  TrophyIcon,
  UsersIcon,
} from './icons';
import { ContactSupport } from './contact-support';
import { RESULTS_WINDOW_DAYS } from '../lib/results-search';
import { cn } from '@/lib/utils';

const navItemBase =
  'relative flex items-center gap-2 px-5 py-[7px] text-[13.5px] font-medium transition-colors';

/** Two digits is all the pill has room for, and «99+» is as actionable as any
 *  exact number past it. The server caps its count at 100 for the same reason. */
const BADGE_MAX = 99;

/** Whether this deployment has a «Кооперация» (supply-request) layer at all —
 *  Uzbekistan's cooperation.uz; Kazakhstan has none. */
const HAS_SUPPLY = countryConfig.features.supplyScope;

/**
 * `badge` is NOT `count` in another colour, and merging them would be a bug.
 *
 * `count` is how big a destination IS («Все тендеры · 2 431») — a stable fact,
 * laid out in the flex flow with `ml-auto`. `badge` is how much of it is new to
 * THIS reader, so it appears and disappears under them. In the flow, arriving
 * would re-truncate the label mid-session; positioned absolutely inside the
 * already-`relative` row it cannot move anything, which is the requirement. The
 * rail is 250px and the only label that carries a badge is «Мои тендеры», so
 * there is nothing for the pill to collide with.
 *
 * The pill is `aria-hidden` and the LINK takes `badgeLabel` as its accessible
 * name instead: a bare "5" announced after «Мои тендеры» says nothing, and
 * leaving the pill inside the name computation would silently rewrite the
 * accessible name of any row that later gains a badge.
 */
function NavRow({
  to,
  icon,
  label,
  count,
  badge,
  badgeLabel,
  end,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  count?: number;
  /** New-since-last-visit pill. Absent, zero or negative renders nothing. */
  badge?: number;
  /** Accessible name for the whole row while the badge is showing. */
  badgeLabel?: string;
  end?: boolean;
}) {
  const showBadge = badge != null && badge > 0;
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={showBadge ? badgeLabel : undefined}
      className={({ isActive }) =>
        `${navItemBase} ${
          isActive
            ? 'bg-brand-dim font-semibold text-brand before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-r before:bg-brand before:content-[""]'
            : 'text-ink-soft hover:bg-bone hover:text-ink'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`h-4 w-4 shrink-0 ${isActive ? 'opacity-100' : 'opacity-70'}`}>
            {icon}
          </span>
          <span className="truncate">{label}</span>
          {count != null && (
            <span
              className={`ml-auto min-w-[22px] rounded-pill px-[7px] text-center text-[11px] font-semibold leading-[1.6] ${
                isActive ? 'bg-brand-dim text-brand' : 'border border-edge bg-bone text-ink-muted'
              }`}
            >
              {count}
            </span>
          )}
          {showBadge && (
            <span
              aria-hidden="true"
              data-testid="nav-badge"
              className="pointer-events-none absolute right-4 top-1/2 min-w-[20px] -translate-y-1/2 rounded-pill bg-brand px-[6px] text-center text-[10px] font-bold leading-[1.7] text-white"
            >
              {badge > BADGE_MAX ? `${BADGE_MAX}+` : badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-5 pb-1.5 pt-[18px] text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
      {children}
    </div>
  );
}

/**
 * Saved-searches list for signed-in users. Falls back to the empty state when
 * the user has none.
 *
 * TWO actions per row, and they are genuinely different questions:
 *  - the NAME navigates to the catalog with the search's filters applied —
 *    "show me what this is matching right now";
 *  - the ✎ opens the builder in place — "change what it matches".
 *
 * Reaching the builder used to cost two hops (row → catalog → «Настроить
 * оповещения») plus a full catalog re-render, which is why the pencil is back.
 * But it does NOT replace the link: a saved search is a SECTION of the user's
 * Telegram digest — each renders as its own «📁 «name» — N новых» block inside
 * one message (`digest-format.ts:194`) — and the common act on a digest
 * section is to go look at it, not to edit it.
 *
 * Deletion lives HERE rather than in the builder: it changes the list, so it
 * belongs beside the list. The builder is for editing one alert and now has a
 * single action (Save) because of it.
 *
 * Two-step, inline: the ✕ turns the row into «Удалить поиск «name»? · Да ·
 * Отмена». A confirm is warranted because an alert stops arriving SILENTLY —
 * the user would not notice the mistake for weeks — but a modal would be
 * heavier than the act deserves in a 250px rail, so the SHAPE stays inline
 * while the WORDS and the mutation are the ones /app/alerts now uses
 * (`saved-search-delete.tsx`). Both surfaces name the search and say what
 * stops; this one used to ask a bare «Удалить оповещение?» that named nothing,
 * which in a list of five rows is not really a confirmation.
 */
function SavedSearchList() {
  const t = useT();
  const [editing, setEditing] = useState<SavedSearch | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const { data: searches } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
  });

  const prompt = useSavedSearchDeletePrompt();
  const remove = useDeleteSavedSearch(() => setConfirming(null));

  if (!searches || searches.length === 0) {
    return <div className="px-5 py-1.5 text-[12.5px] text-ink-muted">{t('shell.saved.empty')}</div>;
  }

  return (
    <>
      {searches.map((s) => (
        <div key={s.id} className="group/saved flex items-center">
          {confirming === s.id ? (
            <div className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-5 pr-2 text-[12.5px]">
              {/* `title` because the rail clips it: the sentence names the
                  search, and a clipped name is the one word that matters. */}
              <span className="min-w-0 flex-1 truncate text-ink-muted" title={prompt(s.name)}>
                {prompt(s.name)}
              </span>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(s.id)}
                className="shrink-0 font-bold text-status-closing-text disabled:opacity-60"
              >
                {t('saved.deleteYes')}
              </button>
              <button
                type="button"
                disabled={remove.isPending}
                onClick={() => setConfirming(null)}
                className="shrink-0 font-semibold text-ink-muted disabled:opacity-60"
              >
                {t('saved.cancel')}
              </button>
            </div>
          ) : (
            <>
              <Link
                to={savedSearchToCatalogPath(s)}
                className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-5 pr-1 text-[12.5px] font-medium text-ink-muted transition-colors hover:bg-bone hover:text-ink-soft"
              >
                <span className="h-1 w-1 shrink-0 rounded-full bg-edge group-hover/saved:bg-brand" />
                <span className="truncate">{s.name}</span>
              </Link>
              <button
                type="button"
                aria-label={`${t('saved.edit')}: ${s.name}`}
                title={t('saved.edit')}
                onClick={() => setEditing(s)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-[12px] leading-none text-ink-muted transition-colors hover:bg-bone hover:text-brand"
              >
                ✎
              </button>
              <button
                type="button"
                aria-label={`${t('saved.delete')}: ${s.name}`}
                title={t('saved.delete')}
                onClick={() => setConfirming(s.id)}
                className="mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded text-[12px] leading-none text-ink-muted transition-colors hover:bg-bone hover:text-status-closing-text"
              >
                ✕
              </button>
            </>
          )}
        </div>
      ))}

      <SavedSearchEditSheet
        search={editing}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
      />
    </>
  );
}

/**
 * m16 left sidebar: brand wordmark, nav sections, saved searches, user footer.
 *
 * `className` is merged onto the root <aside> so the same component serves two
 * hosts: the persistent desktop rail (hidden below `lg` via the app shell) and
 * the mobile off-canvas drawer (where it fills the sheet, borderless).
 */
export function Sidebar({ className }: { className?: string } = {}) {
  const { t, locale } = useLocale();
  const { status, user } = useAuth();
  const authOrigin = useAuthOrigin();

  // "Все тендеры" must match the catalog header total for the default browse
  // scope (active + closing), not the sum of per-region counts (which double
  // counts statuses and drops region-less tenders). Fetch the real total.
  const { data: catalogCount } = useQuery({
    queryKey: ['catalog', 'count'],
    queryFn: () => fetchCatalog({ limit: 1 }),
  });
  // IFI row count: fetched separately (scoped) so the row can hide itself
  // while the layer is empty (sources built but not yet enabled in prod).
  const { data: ifiCount } = useQuery({
    queryKey: ['catalog', 'count', 'ifi'],
    queryFn: () => fetchCatalog({ scope: 'ifi', limit: 1 }),
  });
  // Supply row count (Task 10 — cooperation.uz reverse-auction supply requests):
  // same "hide only once genuinely empty" rule as the МФИ row below. Not even
  // asked for where the deployment has no supply source (`features.supplyScope`,
  // Kazakhstan) — the row is never drawn there.
  const { data: supplyCount } = useQuery({
    queryKey: ['catalog', 'count', 'supply'],
    queryFn: () => fetchCatalog({ scope: 'supply', limit: 1 }),
    enabled: HAS_SUPPLY,
  });

  // Awarded archive. `status: 'closed'` + `awarded` is the same pair /results
  // itself sends — the API's ScopeFilter has no 'awarded' member, so this must
  // NOT be spelled as a scope (it 400s).
  // The month window /results itself lists (owner, 2026-10-07) — the row's
  // count must be the page's count.
  const { data: awardedCount } = useQuery({
    queryKey: ['catalog', 'count', 'awarded', RESULTS_WINDOW_DAYS],
    queryFn: () =>
      fetchCatalog({
        status: 'closed',
        awarded: true,
        outcomeDays: RESULTS_WINDOW_DAYS,
        limit: 1,
      }),
  });

  const totalTenders = catalogCount?.total ?? 0;
  const totalIfi = ifiCount?.total ?? 0;
  const totalSupply = supplyCount?.total ?? 0;
  const totalAwarded = awardedCount?.total ?? 0;

  /*
   * ⚠️ "Not counted yet" is not "empty".
   *
   * The МФИ row was gated on `totalIfi > 0`, and `ifiCount` starts undefined —
   * so on every single load the row was ABSENT for as long as its fetch took,
   * then materialised and shoved «Результаты», «Рынок», «Цены» and everything
   * below it down by one row. A whole navigation item appearing after paint is
   * the largest layout shift on the page, and it happens to every visitor on
   * every page, because the sidebar is part of the shell.
   *
   * The guard it came from is also stale. Its comment says IFI sources are
   * "built but not yet enabled in prod" — five of them have been enabled since
   * July (worldbank, adb, ungm, undp, aiib), and the layer currently holds 86
   * projects. It was protecting against a state that no longer exists, at the
   * cost of a shift on every load.
   *
   * So: hide the row only once the count has RESOLVED and genuinely come back
   * zero. While it is unknown, render it — that matches reality, and the row
   * still self-hides if the layer ever really empties.
   */
  const ifiLayerIsEmpty = ifiCount !== undefined && totalIfi === 0;
  /** Same "not counted yet ≠ empty" reasoning as `ifiLayerIsEmpty` above. */
  const supplyLayerIsEmpty = supplyCount !== undefined && totalSupply === 0;

  const isAuthed = status === 'authed' && user;

  // Count for the favourites row. Reuses the EXACT query key the favourites
  // page already uses (['saved-tenders', locale]) — so React Query dedupes
  // it and the sidebar adds no request of its own. The locale must stay in
  // the key: without it, switching language would serve the previous
  // language's rows to the page from cache.
  //
  // Gated on `isAuthed`: the endpoint requires a session, and firing it for
  // a guest would 401 on every page render.
  const { data: savedTenders } = useQuery({
    queryKey: ['saved-tenders', locale],
    queryFn: () => listSavedTenders(locale),
    enabled: Boolean(isAuthed),
  });
  // Same `['saved-searches']` key `SavedSearchList` below already uses, so this
  // rides that query rather than adding one. Read here only to decide whether
  // the «Мои тендеры» row exists.
  const { data: savedSearches } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: listSavedSearches,
    enabled: Boolean(isAuthed),
  });
  const hasMyTendersRow = Boolean(isAuthed) && (savedSearches?.length ?? 0) >= 2;

  /*
   * «Мои тендеры» badge: matches that arrived since this reader last opened
   * the page.
   *
   * The cut-off is the SAME localStorage stamp the page itself writes
   * (`my-tenders:seen:<userId>`) — read here, written there. A second marker,
   * or a server-side field, would be a second definition of "seen" and the two
   * would disagree the first time one of them failed to update.
   *
   * Read on every render rather than frozen in state, unlike on the page: the
   * sidebar is mounted while the reader visits «Мои тендеры», and it is the
   * stamp MOVING that must make the badge fall to zero. The page invalidates
   * this key on mount to force exactly that.
   *
   * NO stamp means no badge and no request. That is not a fallback, it is
   * agreement: the page's «новое» dot also requires `seenBefore !== null`, so a
   * reader who has never opened it sees no dots there — a badge promising
   * «99+ новых» over a page that marks nothing new is the number lying.
   *
   * Skipped entirely for guests and for anyone the row is hidden from, so the
   * 87 accounts that can see it are the only ones who ever call the endpoint.
   */
  const seenAt = isAuthed && user ? readLastVisit(user.id) : null;
  const { data: newMatches } = useQuery({
    queryKey: ['my-tenders', 'new-count', user?.id ?? '', seenAt],
    queryFn: () => fetchNewMatchCount(new Date(seenAt as number)),
    enabled: hasMyTendersRow && seenAt !== null,
    // The feed moves on the ingestion cycle (~2h), not on the reader. Two
    // minutes is far finer than the data and still spares a poll per route
    // change; focus refetch covers the "came back to the tab" case.
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const initials = isAuthed ? userInitials(user) : 'G';

  return (
    <aside
      className={cn(
        'flex h-[100dvh] flex-col overflow-hidden border-r border-edge bg-bone-surface',
        className,
      )}
    >
      {/* Brand */}
      <div className="flex items-center gap-2 border-b border-edge-light px-5 pb-[18px] pt-5">
        <Link to="/" className="flex items-center gap-2">
          <BrandTile size="sm" />
          <span className="font-brand text-[17px] font-extrabold leading-none tracking-[-0.3px] text-ink">
            {countryConfig.brand.name}
          </span>
        </Link>
        {/* Country badge (NOT a language switcher) — the deployment's country code,
            so KazTender reads «KZ», UzTender «UZ». Was hardcoded "UZ". */}
        <span className="ml-auto rounded bg-brand-dim px-[5px] py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] text-brand">
          {countryConfig.code}
        </span>
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden pb-2">
        <SectionLabel>{t('shell.section.catalog')}</SectionLabel>
        {/* «Мои тендеры» — the FIRST row of the whole menu (2026-09-21, Igor).
            It used to open the personal sub-run further down (2026-09-13); the
            owner's call now is that the page answering «что мне сегодня
            подходит» is the one a returning reader comes back for, so it sits
            above «Все тендеры» rather than below the catalogue rows that feed
            it. Nothing else about the order moved.

            TWO active searches or the row does not appear. With one search this
            page is the catalog with a worse URL: the union is a single
            subscription and the «по поиску» label names the only thing it could
            name. The route enforces the same rule with a redirect, so a stale
            bookmark cannot get around a hidden row. Rendered only once the list
            has RESOLVED — `savedSearches` starts undefined and gating on
            `>= 2` alone would shift the whole nav on every load, the defect the
            МФИ row below documents at length. */}
        {hasMyTendersRow && (
          <NavRow
            to="/app/my-tenders"
            icon={<ListIcon className="h-full w-full" />}
            label={t('myTenders.title')}
            badge={newMatches}
            badgeLabel={t('myTenders.newCountAria', {
              count: (newMatches ?? 0) > BADGE_MAX ? `${BADGE_MAX}+` : (newMatches ?? 0),
            })}
          />
        )}
        <NavRow
          to="/catalog"
          end
          icon={<ListIcon className="h-full w-full" />}
          label={t('shell.nav.allTenders')}
          count={totalTenders > 0 ? totalTenders : undefined}
        />
        {/* Hidden while the layer is empty (IFI sources are built but not yet
            enabled in prod) — an empty nav destination reads as broken. The
            check lives here, not inside NavRow, since NavRow is shared with
            regions/categories and must not change behaviour for them. */}
        {!ifiLayerIsEmpty && (
          <NavRow
            to="/ifi"
            icon={<BankIcon className="h-full w-full" />}
            label={t('shell.nav.ifiProjects')}
            count={totalIfi > 0 ? totalIfi : undefined}
          />
        )}
        {/* Task 10 — cooperation.uz reverse-auction supply requests: a separate,
            opt-in, deliberately `noindex` section (thin, budgetless leads — see
            lib/tender-status.ts). Same empty-layer hiding rule as МФИ above.
            Only where the country ingests such a source: kaztender.com drew
            «Кооперация» into every prerendered page (audit 2026-10-16), since
            the count is not known at prerender time. */}
        {HAS_SUPPLY && !supplyLayerIsEmpty && (
          <NavRow
            to="/supply"
            icon={<PackageIcon className="h-full w-full" />}
            label={t('shell.nav.supplyRequests')}
            count={totalSupply > 0 ? totalSupply : undefined}
          />
        )}
        {/* The awarded-lot archive, kept next to the other two TENDER listings
            rather than below the analytics pages — all three answer "which
            tenders", where /market and /prices answer "what do things cost".

            This row is also what makes the outcomes tree crawlable AT ALL:
            /results is the root of ~4k lots (≈8k URLs across both locales) that
            are indexable and in the sitemap but, before it existed, had no
            inbound internal link from anywhere — browse defaults to
            active+closing, and /market and /prices/:ikpu link to zero tenders.
            Drop this row and the whole subtree goes back to being orphaned. */}
        <NavRow
          to="/results"
          icon={<TrophyIcon className="h-full w-full" />}
          label={t('nav.results')}
          count={totalAwarded > 0 ? totalAwarded : undefined}
        />
        {/* /market and /prices are built on the ИКПУ taxonomy + years of BidHistory
            — real on UZ, near-empty on a deployment whose classifier is a stub with
            no price history (KZ). Gated on the country's `features.priceIntel` so the
            KZ nav doesn't offer two dead-end analytics pages (same reasoning as the
            empty-layer hiding for /ifi and /supply above). */}
        {countryConfig.features.priceIntel && (
          <>
            <NavRow
              to="/market"
              icon={<ChartIcon className="h-full w-full" />}
              label={t('nav.market')}
            />
            <NavRow
              to="/prices"
              icon={<TagIcon className="h-full w-full" />}
              label={t('nav.prices')}
            />
          </>
        )}
        <NavRow
          to="/companies"
          icon={<BuildingIcon className="h-full w-full" />}
          label={t('nav.companies')}
        />
        {/* Onboarding: newcomers ask «как участвовать» — surface the supplier
            guide site-wide, not just in the footer. */}
        <NavRow to="/guide" icon={<BookIcon className="h-full w-full" />} label={t('nav.guide')} />
        <NavRow
          to="/app/favorites"
          icon={<StarIcon className="h-full w-full" />}
          label={t('shell.nav.favorites')}
          count={savedTenders?.length || undefined}
        />
        <NavRow
          to="/app/alerts"
          icon={<BellIcon className="h-full w-full" />}
          label={t('shell.nav.alerts')}
        />
        {/* Team roster: only reachable by a paying user — a free visitor has
            no seats to manage and would only hit plan_has_no_seats. Any paid
            plan is enough (not just Бизнес): team.tsx's own empty state
            already tells a seatless Pro payer that's what they're on.
            Gated on the country's `features.teamPlans` — a deployment with no
            «Бизнес» seat tier (KZ) has no team feature to surface at all. */}
        {countryConfig.features.teamPlans && isPaidUser(user) && (
          <NavRow
            to="/app/team"
            icon={<UsersIcon className="h-full w-full" />}
            label={t('team.pageTitle')}
          />
        )}

        {/* Subscription — paid users get NO other in-app path to manage their
            plan (the /plans upsell below is guest/free-only), so surface it here. */}
        {isPaidUser(user) && (
          <NavRow
            to="/plans"
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-full w-full"
                aria-hidden
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
            }
            label={t('nav.subscription')}
          />
        )}

        {/* Help/About: kept in the primary nav (not buried in the footer) so
            they're easy to find — right after Alerts, styled like the other
            nav rows (icon + label). */}
        <ContactSupport
          className={`${navItemBase} w-full text-left text-ink-soft hover:bg-bone hover:text-ink`}
        >
          <span className="h-4 w-4 shrink-0 opacity-70" aria-hidden>
            <ChatIcon className="h-full w-full" />
          </span>
          <span className="truncate">{t('support.trigger')}</span>
        </ContactSupport>
        <Link to="/about" className={`${navItemBase} text-ink-soft hover:bg-bone hover:text-ink`}>
          <span className="h-4 w-4 shrink-0 opacity-70" aria-hidden>
            <InfoIcon className="h-full w-full" />
          </span>
          <span className="truncate">{t('nav.about')}</span>
        </Link>

        <div className="mx-4 my-2 h-px bg-edge-light" />

        {/*
         * Saved searches — for signed-in users only.
         *
         * A guest used to see «Популярные подборки» here: a heading plus two
         * hard-coded links («Дорожные проекты > 1B», «IT тендеры»). They were
         * kept as the last internal path to the prerendered /category hubs
         * after a rename stopped them pretending to be the visitor's own saved
         * searches — but two editorial picks frozen in a locale file are not a
         * navigation surface, and /directory, the category chips and the
         * per-tender category links all reach those hubs now. Removed
         * 2026-09-21 (owner), leaving the one thing this block was ever for:
         * an invitation to save a search.
         */}
        {isAuthed && (
          <>
            <SectionLabel>{t('shell.section.savedSearches')}</SectionLabel>
            <SavedSearchList />
          </>
        )}
        {!isAuthed && (
          <Link
            to="/register"
            className="mt-1 flex items-center gap-2 px-5 py-1.5 text-[12.5px] font-semibold text-brand transition-colors hover:bg-bone"
          >
            <span aria-hidden="true">+</span>
            {t('shell.saved.guestCta')}
          </Link>
        )}
      </nav>

      {/* Footer: user row + upgrade prompt */}
      <div className="border-t border-edge bg-bone-surface p-4">
        {isAuthed ? (
          <Link
            to="/app"
            className="flex items-center gap-2.5 rounded-md p-2 transition-colors hover:bg-bone"
          >
            <span className="relative shrink-0">
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="h-7 w-7 rounded-lg object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand to-[#0d9090] text-[11px] font-bold text-white">
                  {initials}
                </span>
              )}
              {isPaidUser(user) && <ProBadge size={12} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-semibold leading-[1.3] text-ink">
                {userDisplayName(user)}
              </span>
              <span
                className={`block text-[11px] font-semibold leading-[1.3] ${
                  isPaidUser(user) ? 'text-brand' : 'text-ink-muted'
                }`}
              >
                {isPaidUser(user)
                  ? t('shell.user.paidPlan', { plan: planLabel(user) ?? 'Pro' })
                  : t('shell.user.freePlan')}
              </span>
            </span>
            <span className="text-[10px] text-ink-muted">⌄</span>
          </Link>
        ) : (
          // Guests: register is the primary CTA (they aren't registered yet);
          // login sits below as the secondary action for returning users.
          <div className="flex flex-col gap-1.5">
            {/* Carry the page they are leaving (`useAuthOrigin`), so a visitor
                who opens the drawer on a tender is returned to that tender
                after signing in — see `postAuthPath`. */}
            <Link
              to="/register"
              state={authOrigin}
              className="inline-flex h-9 items-center justify-center rounded-md bg-brand px-3 text-[12.5px] font-bold text-white transition-colors hover:bg-brand-mid"
            >
              {t('login.signUp')}
            </Link>
            <Link
              to="/login"
              state={authOrigin}
              className="inline-flex h-8 items-center justify-center rounded-md text-[12px] font-semibold text-ink-soft transition-colors hover:bg-bone hover:text-ink"
            >
              {t('nav.login')}
            </Link>
          </div>
        )}

        {/* Pro upsell — the sidebar's one monetisation surface, so it is sized
            to be read rather than skimmed past: the label carries the weight and
            the arrow marks it as a destination, not a notice. */}
        {!isPaidUser(user) && (
          <Link
            to="/plans"
            className="mt-2 block cursor-pointer rounded-lg border border-brand/25 bg-gradient-to-br from-brand/[0.09] to-brand-mid/[0.06] p-4 transition-colors hover:border-brand/40 hover:bg-brand/[0.12]"
          >
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="font-brand text-[14px] font-extrabold tracking-[-0.1px] text-brand">
                {t('shell.upgrade.label')}
              </span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
            </div>
            <div className="text-[12px] leading-[1.45] text-ink-muted">
              {t('shell.upgrade.desc')}
            </div>
          </Link>
        )}
      </div>
    </aside>
  );
}
